<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />
      <div class="profile-section mb-2">
          <div class="pl-2">
              <span>
                  <a class="text-black" href="javascript:void(0)" @click="$router.push({ path: '/kids-device', query:{ profileId: this.profileId }})">
                    <i class="fas fa-long-arrow-alt-left"></i> <b>Home</b>
                  </a>
              </span>
          </div>
      </div>

    <!-- Device Options -->
    <div class="card shadow-sm mb-3">
        <div class="card-body">
            <h6 class="card-title text-black mb-3">
              <i class="fas fa-cogs text-primary me-2"></i> Device Options
            </h6>
            
            <!-- Action Buttons -->
            <div class="action-buttons-row">
              <!-- Lock Device -->
              <button
                class="btn btn-compact btn-warning"
                :disabled="isDisabled"
                @click="sendDeviceCommand('LOCK')"
              >
                <i class="fas fa-lock me-1"></i>
                Lock Device
                <i
                  v-if="isDisabled && currentAction === 'LOCK'"
                  class="fa fa-spinner fa-spin ms-1"
                ></i>
              </button>

              <!-- Reboot Device -->
              <button
                class="btn btn-compact btn-secondary"
                :disabled="isDisabled"
                @click="sendDeviceCommand('REBOOT')"
              >
                <i class="fas fa-sync-alt me-1"></i>
                Reboot Device
                <i
                  v-if="isDisabled && currentAction === 'REBOOT'"
                  class="fa fa-spinner fa-spin ms-1"
                ></i>
              </button>

              <!-- Current Screenshot Device -->
              <button
                class="btn btn-compact btn-primary"
                :disabled="isDisabled"
                @click="sendFcmCommand()"
              >
                <i class="fas fa-camera me-1"></i>
                Get Current Screen
                <i
                  v-if="isDisabled && currentAction === 'GET_CURRENT_SCREEN'"
                  class="fa fa-spinner fa-spin ms-1"
                ></i>
              </button>
            </div>
        </div>
    </div>

    <!-- Redirect Countdown Notice -->
    <div v-if="showRedirectNotice" class="card shadow-sm mb-3 redirect-notice-card">
      <div class="card-body">
        <div class="redirect-content">
          <div class="redirect-icon">
            <i class="fas fa-images"></i>
          </div>
          <div class="redirect-text">
            <h6 class="mb-1">Screenshot Capture In Progress</h6>
            <p class="mb-0">
              Redirecting to screenshots page in <strong>{{ countdown }}</strong> second{{ countdown !== 1 ? 's' : '' }}...
            </p>
          </div>
          <button class="btn btn-sm btn-outline-secondary" @click="cancelRedirect">
            Cancel
          </button>
        </div>
        <div class="progress mt-3" style="height: 4px;">
          <div 
            class="progress-bar bg-primary" 
            role="progressbar" 
            :style="{ width: progressWidth + '%' }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Phone & Network - Highlighted Section (Only show if phone number exists) -->
    <div class="card shadow-sm mb-4 phone-network-card" v-if="deviceDetails.phoneNumber">
      <div class="card-body">
        <div class="phone-network-content">
          <div class="phone-icon-wrapper">
            <i class="fas fa-phone-alt"></i>
          </div>
          <div class="phone-details">
            <div class="network-name">{{ deviceDetails.networkOperator || 'Unknown Network' }}</div>
            <a 
              :href="'tel:' + deviceDetails.phoneNumber" 
              class="phone-number"
              @click="trackPhoneClick"
            >
              <i class="fas fa-phone me-2"></i>{{ formatPhoneNumber(deviceDetails.phoneNumber) }}
            </a>
          </div>
          <div class="call-action">
            <a 
              :href="'tel:' + deviceDetails.phoneNumber"
              class="btn btn-call"
              @click="trackPhoneClick"
            >
              <i class="fas fa-phone-volume"></i>
              <span>Call Now</span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Device Details -->
    <div class="card shadow-sm mb-4 device-details-card">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="card-title text-black mb-0">
            <i class="fas fa-info-circle text-primary me-2"></i> Device Details
          </h5>
          <button class="btn btn-sm btn-primary btn-sync" :disabled="isDisabled" @click="syncDeviceDetails()">
            <i class="fas fa-sync-alt me-1"></i> Sync
            <span v-if="isDisabled" class="ms-2">
              <i class="fa fa-spinner fa-spin"></i>
            </span>
          </button>
        </div>
        <hr>
        <p class="text-muted mb-4">
          View detailed technical information about the child's device, including system version, hardware, network status, and memory capacity.
        </p>

        <div class="detail-grid">
          <!-- Battery & Status -->
          <div class="detail-section">
            <h6 class="detail-section-title">
              <i class="fas fa-battery-three-quarters me-2"></i>Battery & Status
            </h6>
            <div class="detail-item">
              <i class="fas fa-battery-half icon"></i>
              <span><strong>Battery Level:</strong> {{ deviceDetails.batteryLevel }}%</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-clock icon"></i>
              <span><strong>Last Checked:</strong> {{ deviceDetails.batteryCheckedAt }}</span>
            </div>
          </div>

          <!-- Device Identity -->
          <div class="detail-section">
            <h6 class="detail-section-title">
              <i class="fas fa-id-card me-2"></i>Device Identity
            </h6>
            <div class="detail-item">
              <i class="fas fa-barcode icon"></i>
              <span><strong>Serial Number:</strong> {{ deviceDetails.serialNumber }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-fingerprint icon"></i>
              <span><strong>IMEI:</strong> {{ deviceDetails.imei }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-wifi icon"></i>
              <span><strong>WiFi MAC:</strong> {{ deviceDetails.wifiMac }}</span>
            </div>
          </div>

          <!-- Hardware Info -->
          <div class="detail-section">
            <h6 class="detail-section-title">
              <i class="fas fa-microchip me-2"></i>Hardware
            </h6>
            <div class="detail-item">
              <i class="fas fa-mobile-alt icon"></i>
              <span><strong>Model:</strong> {{ deviceDetails.model }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-industry icon"></i>
              <span><strong>Brand:</strong> {{ deviceDetails.brand }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-cogs icon"></i>
              <span><strong>Manufacturer:</strong> {{ deviceDetails.manufacturer }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-microchip icon"></i>
              <span><strong>Hardware:</strong> {{ deviceDetails.hardware }}</span>
            </div>
          </div>

          <!-- System Info -->
          <div class="detail-section">
            <h6 class="detail-section-title">
              <i class="fab fa-android me-2"></i>System
            </h6>
            <div class="detail-item">
              <i class="fab fa-android icon"></i>
              <span><strong>Android Version:</strong> {{ deviceDetails.androidVersion }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-signal icon"></i>
              <span><strong>Baseband:</strong> {{ deviceDetails.basebandVersion }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-globe icon"></i>
              <span><strong>Language:</strong> {{ deviceDetails.languageCode }}</span>
            </div>
          </div>

          <!-- Storage & Memory -->
          <div class="detail-section">
            <h6 class="detail-section-title">
              <i class="fas fa-hdd me-2"></i>Storage & Memory
            </h6>
            <div class="detail-item">
              <i class="fas fa-memory icon"></i>
              <span><strong>RAM:</strong> {{ deviceDetails.totalRam }}</span>
            </div>
            <div class="detail-item">
              <i class="fas fa-hdd icon"></i>
              <span><strong>Storage:</strong> {{ deviceDetails.totalStorage }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import KidHeader from '@/components/KidHeader.vue'
import KidFooter from '@/components/KidFooter.vue'
import KidsService from "../../services/KidsService";
import ProfileService from "../../services/ProfileService";

export default {
  name: 'DeviceOptions',
  components: {
    KidHeader,
    KidFooter,
  },
  data() {
    return {
        profileId: this.$route.query.profileId || this.$route.query.profile_id,
        serialNumber: this.$route.query.serialNumber,
        isDisabled: false,
        currentAction: '',
        deviceDetails: {},
        showRedirectNotice: false,
        countdown: 5,
        countdownInterval: null,
        progressWidth: 0
    }
  },
  methods: {
    formatPhoneNumber(number) {
      if (!number) return '';
      const cleaned = ('' + number).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
      }
      return number;
    },
    trackPhoneClick() {
      console.log('Phone call initiated:', this.deviceDetails.phoneNumber);
    },
    async getDeviceInfo() {
        const data = {
            serialNumber: this.$route.query.serialNumber
        };

        await KidsService.getDeviceInfo(data)
        .then((response) => {
          this.deviceDetails = response.data.data
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
          this.isDisabled = false;
        });
    }, 
    async syncDeviceDetails() {
        this.isDisabled = true;
        const data = {
            id: this.deviceDetails.id
        };

        await KidsService.syncDeviceDetails(data)
        .then((response) => {
          this.$toast.success(response.data.respones_msg);
          this.getDeviceInfo();
          this.isDisabled = false;
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
          this.isDisabled = false;
        });
    }, 
    async sendDeviceCommand(action) {
        this.isDisabled = true;
        this.currentAction = action;
        const data = {
            type: action,
            serialNumber: this.serialNumber
        };

        try {
            await KidsService.sendDeviceCommand(data);
            this.$toast.success(`${action} command sent successfully.`);
        } catch (err) {
            this.$toast.error(err.response?.data?.respones_msg || 'Failed to send command.');
        } finally {
            this.isDisabled = false;
            this.currentAction = '';
        }
    },
    async sendFcmCommand() {
        this.isDisabled = true;
        this.currentAction = 'GET_CURRENT_SCREEN';
        let data = {
            title: "Send current screenshot",
            message: "Send current screenshot",
            packageName: "",
            action: "GET_CURRENT_SCREEN",
            serialNumber: this.serialNumber
        }
        await KidsService.sendFcmCommand(data)
        .then((response) => {
          this.$toast.success("Screenshot capture command sent!");
          this.isDisabled = false;
          this.currentAction = '';
          
          // Start countdown and redirect
          this.startRedirectCountdown();
        }).catch((e) => {
          this.$toast.error(e.response.data.respones_msg);
          this.isDisabled = false;
          this.currentAction = '';
        });
    },
    startRedirectCountdown() {
      this.showRedirectNotice = true;
      this.countdown = 5;
      this.progressWidth = 0;
      
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        this.progressWidth = ((5 - this.countdown) / 5) * 100;
        
        if (this.countdown <= 0) {
          this.clearCountdown();
          this.redirectToScreenshots();
        }
      }, 1000);
    },
    clearCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    cancelRedirect() {
      this.clearCountdown();
      this.showRedirectNotice = false;
      this.countdown = 5;
      this.progressWidth = 0;
      this.$toast.info('Redirect cancelled');
    },
    redirectToScreenshots() {
      this.$router.push({
        path: '/device-screenshots',
        query: {
          profileId: this.profileId,
          serialNumber: this.serialNumber
        }
      });
    }
  },
  beforeMount() {
    this.getDeviceInfo();
  },
  beforeUnmount() {
    this.clearCountdown();
  }
}
</script>

<style scoped>
.card {
  border-radius: 12px;
  border: 1px solid #e0e0e0;
}

/* Action Buttons - Compact Style */
.action-buttons-row {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
}

.btn-compact {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex: 1;
  min-width: fit-content;
  justify-content: center;
}

.btn-compact i {
  font-size: 0.9rem;
}

.btn-compact:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.btn-compact:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-compact.btn-warning {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #000;
}

.btn-compact.btn-warning:hover:not(:disabled) {
  background-color: #e0a800;
  border-color: #d39e00;
}

.btn-compact.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

.btn-compact.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
  border-color: #545b62;
}

.btn-compact.btn-primary {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

.btn-compact.btn-primary:hover:not(:disabled) {
  background-color: #0069d9;
  border-color: #0062cc;
}

/* Redirect Notice Card */
.redirect-notice-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.redirect-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.redirect-icon {
  background: rgba(255, 255, 255, 0.2);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.redirect-icon i {
  font-size: 1.5rem;
}

.redirect-text {
  flex: 1;
}

.redirect-text h6 {
  color: white;
  font-weight: 600;
}

.redirect-text p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.redirect-notice-card .btn-outline-secondary {
  background: rgba(255, 255, 255, 0.2);
  border-color: white;
  color: white;
}

.redirect-notice-card .btn-outline-secondary:hover {
  background: white;
  color: #667eea;
}

.redirect-notice-card .progress {
  background: rgba(255, 255, 255, 0.2);
}

.redirect-notice-card .progress-bar {
  background: white !important;
}

/* Phone & Network Card - Highlighted */
.phone-network-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
}

.phone-network-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.5);
}

.phone-network-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 1.5rem;
  align-items: center;
}

.phone-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.phone-icon-wrapper i {
  font-size: 2rem;
  color: white;
}

.phone-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.network-name {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.phone-number {
  font-size: 1.8rem;
  font-weight: 700;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  letter-spacing: 1px;
}

.phone-number:hover {
  transform: translateX(5px);
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.call-action {
  display: flex;
  align-items: center;
}

.btn-call {
  background: white;
  color: #667eea;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  text-decoration: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.btn-call:hover {
  background: #f8f9fa;
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  color: #5568d3;
}

.btn-call i {
  font-size: 1.1rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* Device Details Card */
.device-details-card {
  background-color: #fff;
}

.btn-sync {
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-sync:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.detail-section {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 1.2rem;
  border-left: 4px solid #4a90e2;
  transition: all 0.3s ease;
}

.detail-section:hover {
  background: #f0f2f5;
  transform: translateX(3px);
}

.detail-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  align-items: center;
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.6;
  margin-bottom: 0.8rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.detail-item .icon {
  color: #4a90e2;
  min-width: 18px;
  font-size: 1rem;
  margin-top: 2px;
}

.detail-item strong {
  color: #2c3e50;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .phone-network-content {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 1rem;
  }

  .phone-icon-wrapper {
    margin: 0 auto;
  }

  .phone-number {
    justify-content: center;
    font-size: 1.4rem;
  }

  .call-action {
    justify-content: center;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }

  .redirect-content {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 576px) {
  .action-buttons-row {
    flex-direction: column;
  }

  .btn-compact {
    width: 100%;
    justify-content: center;
  }

  .phone-number {
    font-size: 1.2rem;
  }

  .btn-call {
    width: 100%;
    justify-content: center;
  }
}
</style>